/**
 * Same-board duplicate persons — clears person records that are one human on
 * the same board. Dry run first: each group shows the record kept (earliest
 * stated start), the records merged into it, and any tenure the merge would
 * drop. Groups that would lose an earlier start are left unticked.
 */
import { useState } from 'react';
import { UserCheck, Eye, Play, Loader2, AlertTriangle } from 'lucide-react';
import {
  proposeSameBoardMerges, applySameBoardMerges,
  type SameBoardDryRun, type SameBoardApplyResult,
} from '../lib/api/same-board.js';

export function SameBoardMergePanel() {
  const [running, setRunning] = useState<'dry' | 'apply' | null>(null);
  const [dry, setDry] = useState<SameBoardDryRun | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState<SameBoardApplyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | undefined>();

  const runDry = async () => {
    setRunning('dry'); setError(null); setApplied(null);
    try {
      const r = await proposeSameBoardMerges();
      setDry(r);
      setPicked(new Set(r.proposals.filter(p => !p.earlier_start_lost).map(p => p.group_key)));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally { setRunning(null); }
  };

  const runApply = async () => {
    if (!dry) return;
    const chosen = dry.proposals.filter(p => picked.has(p.group_key));
    const persons = chosen.reduce((n, p) => n + p.losers.length, 0);
    if (!chosen.length) return;
    if (!window.confirm(`Merge ${persons} person record${persons === 1 ? '' : 's'} into ${chosen.length} kept record${chosen.length === 1 ? '' : 's'}?\n\nEach merge is archived in Cynapse and re-points Assayer first.`)) return;
    setRunning('apply'); setError(null); setDone(undefined);
    try {
      const r = await applySameBoardMerges(
        chosen.map(p => ({ survivor_id: p.survivor.entity_id, loser_ids: p.losers.map(l => l.entity_id) })),
        st => setDone(st.progress?.done));
      setApplied(r); setDry(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally { setRunning(null); }
  };

  const toggle = (k: string) => setPicked(prev => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-grey-500"><UserCheck className="h-5 w-5" /></div>
        <div className="flex-1">
          <h3 className="font-semibold text-grey-900">Same-board duplicate people</h3>
          <p className="text-sm text-grey-600 mt-1">
            Two person records that are one human on the same board. Keeps the record with the earliest start a
            filing states; the others merge into it. Run the dry run, untick anything doubtful, then merge.
            Re-run the director notices job afterwards.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runDry} disabled={!!running}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50">
            {running === 'dry' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Dry run
          </button>
          <button onClick={runApply} disabled={!!running || !dry || picked.size === 0}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {running === 'apply' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Merge {picked.size || ''} ticked
          </button>
        </div>
      </div>
      {running && <p className="mt-3 text-xs text-grey-500">Running{done ? ` — ${done} groups so far` : ''}…</p>}
      {error && <p className="mt-3 text-xs text-red-700">{error}</p>}
      {applied && (
        <div className="mt-3 rounded bg-grey-50 px-3 py-2 text-xs text-grey-700">
          <span className="font-medium text-grey-800">Merged:</span> {applied.merged} of {applied.groups} groups
          {applied.results.filter(r => !r.merged).map(r => (
            <div key={r.survivor_id} className="text-red-700">{r.survivor_id.slice(0, 8)}: {r.error}</div>
          ))}
        </div>
      )}
      {dry && (
        <div className="mt-3">
          <p className="text-xs text-grey-600 mb-2">
            {dry.groups} groups · {dry.persons_to_merge} records to merge · {dry.groups_losing_an_earlier_start} would lose an earlier start (left unticked)
          </p>
          <ul className="space-y-1.5 max-h-[32rem] overflow-y-auto pr-1">
            {dry.proposals.map(p => (
              <li key={p.group_key} className={`rounded border p-2 text-xs ${p.earlier_start_lost ? 'border-amber-300 bg-amber-50/40' : 'border-grey-200'}`}>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" checked={picked.has(p.group_key)} onChange={() => toggle(p.group_key)} />
                  <span className="flex-1">
                    <span className="font-medium text-grey-900">{p.codes.join(', ')}</span>{' · keep '}
                    <span className="font-medium">{p.survivor.name}</span>
                    <span className="text-grey-400"> (first seen {p.survivor.first_seen?.slice(0, 10) ?? '—'})</span>
                    {' · merge '}{p.losers.map(l => `${l.name} (${l.first_seen?.slice(0, 10) ?? '—'})`).join(', ')}
                    <span className="block text-grey-500">{p.reason}</span>
                    {p.dropped_tenures.map((d, i) => (
                      <span key={i} className={`block ${d.earlier_start_lost ? 'text-amber-800' : 'text-grey-500'}`}>
                        {d.earlier_start_lost && <AlertTriangle className="inline h-3 w-3 mr-1" />}
                        drops {d.role} from {d.loser_valid_from ?? '—'} (kept record has it from {d.survivor_valid_from ?? '—'})
                      </span>
                    ))}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
